/*
 * Removes bundle-price add-ons from the cart when no sofa is left in it.
 * Runs on page load, after every cart change and whenever the cart drawer opens.
 */
(() => {
  if (window.GH2BundleGuard) return;

  const configElement = document.getElementById('BundleGuardConfig');
  if (!configElement) return;

  let config;
  try {
    config = JSON.parse(configElement.textContent);
  } catch (e) {
    return;
  }

  const sofaIds = new Set((config.sofaProductIds || []).map(Number));
  const bundleIds = new Set((config.bundleProductIds || []).map(Number).filter(Boolean));
  if (!sofaIds.size) console.warn('[Bundle guard] no sofa products found; relying on cart line markers');

  const routes = window.routes || {};
  const cartUrl = routes.cart_url || '/cart';
  const cartUpdateUrl = routes.cart_update_url || '/cart/update';

  const isBundleLine = (item) =>
    bundleIds.has(Number(item.product_id)) || Boolean(item.properties && item.properties._bundle_addon);
  const isSofaLine = (item) =>
    sofaIds.has(Number(item.product_id)) || Boolean(item.properties && item.properties._bundle_parent);

  const getCart = () =>
    fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } }).then((response) => response.json());

  const sectionTargets = () => {
    const targets = [];
    document.querySelectorAll('cart-drawer-items, cart-items').forEach((element) => {
      if (typeof element.getSectionsToRender !== 'function') return;
      try {
        element.getSectionsToRender().forEach((section) => {
          if (section.section && !targets.some((target) => target.id === section.id)) targets.push(section);
        });
      } catch (e) {
        /* section not on this page */
      }
    });
    return targets;
  };

  const updateCart = async (updates) => {
    const targets = sectionTargets();
    const payload = { updates };
    if (targets.length) {
      payload.sections = [...new Set(targets.map((target) => target.section))];
      payload.sections_url = window.location.pathname;
    }

    const response = await fetch(`${cartUpdateUrl}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    const cart = await response.json();
    if (cart.status) throw new Error(cart.description || cart.message || 'Cart update failed');

    const empty = cart.item_count === 0;
    document.querySelector('cart-drawer')?.classList.toggle('is-empty', empty);
    document.getElementById('main-cart-footer')?.classList.toggle('is-empty', empty);
    document.querySelector('cart-items')?.classList.toggle('is-empty', empty);

    if (cart.sections) {
      targets.forEach((target) => {
        const html = cart.sections[target.section];
        const container = document.getElementById(target.id);
        if (!html || !container) return;
        const element = (target.selector && container.querySelector(target.selector)) || container;
        const source = new DOMParser().parseFromString(html, 'text/html').querySelector(target.selector || '.shopify-section');
        if (source) element.innerHTML = source.innerHTML;
      });
    }
    return cart;
  };

  const showNotice = (message) => {
    if (!message) return;
    let notice = document.getElementById('BundleGuardNotice');
    if (!notice) {
      notice = document.createElement('div');
      notice.id = 'BundleGuardNotice';
      notice.setAttribute('role', 'status');
      notice.style.cssText = [
        'position:fixed',
        'left:50%',
        'bottom:2.4rem',
        'transform:translateX(-50%)',
        'z-index:1000',
        'max-width:calc(100% - 3.2rem)',
        'padding:1.2rem 1.8rem',
        'border-radius:1.2rem',
        'background:rgb(var(--color-foreground, 18 18 18))',
        'color:rgb(var(--color-background, 255 255 255))',
        'font-size:1.4rem',
        'line-height:1.4',
        'box-shadow:0 0.8rem 2.4rem rgba(0,0,0,0.18)',
        'transition:opacity 0.2s ease',
      ].join(';');
      document.body.appendChild(notice);
    }
    notice.textContent = message;
    notice.style.opacity = '1';
    clearTimeout(notice.hideTimer);
    notice.hideTimer = setTimeout(() => {
      notice.style.opacity = '0';
    }, 5000);
  };

  const state = { running: null, again: false };

  const check = () => {
    if (state.running) {
      state.again = true;
      return state.running;
    }
    state.running = (async () => {
      try {
        do {
          state.again = false;
          const cart = await getCart();
          const bundleLines = cart.items.filter(isBundleLine);
          if (!bundleLines.length || cart.items.some(isSofaLine)) continue;

          const updates = {};
          bundleLines.forEach((line) => {
            updates[line.key] = 0;
          });
          console.info('[Bundle guard] removing bundle add-ons, no sofa in cart', Object.keys(updates));
          const updated = await updateCart(updates);
          showNotice(config.message);
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bundle-guard', cartData: updated });
          }
        } while (state.again);
      } catch (e) {
        console.error(e);
      } finally {
        state.running = null;
      }
    })();
    return state.running;
  };

  const init = () => {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event && (event.source === 'bundle-guard' || event.source === 'product-form')) return;
        check();
      });
    }

    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      let wasOpen = drawer.classList.contains('active');
      new MutationObserver(() => {
        const isOpen = drawer.classList.contains('active');
        if (isOpen && !wasOpen) check();
        wasOpen = isOpen;
      }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }

    check();
  };

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) check();
  });

  window.GH2BundleGuard = { check };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
